import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isCollecting, recordSEO, recordStructuredData } from '@/lib/seoCollector';


interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  noindex?: boolean;
  openGraph?: {
    title?: string;
    description?: string;
    image?: string;
    imageAlt?: string;
    type?: string;
  };
  structuredData?: object;
}

const DEFAULT_SOCIAL_IMAGE = "https://wtrrestoration.com/images/hero-restoration.webp";

export const useSEO = ({
  title,
  description,
  keywords,
  canonical,
  noindex = false,
  openGraph,
  structuredData
}: SEOProps) => {
  const location = useLocation();

  // Build-time prerender: record metadata synchronously so it lands in the
  // static HTML. No-op in the browser.
  if (isCollecting()) {
    const canonicalUrl = canonical || `https://wtrrestoration.com${location.pathname}`;
    recordSEO({
      title,
      description,
      keywords,
      canonical: canonicalUrl,
      noindex,
      ogTitle: openGraph?.title || title,
      ogDescription: openGraph?.description || description,
      ogImage: openGraph?.image || DEFAULT_SOCIAL_IMAGE,
      ogImageAlt: openGraph?.imageAlt || title,
      ogType: openGraph?.type || 'website',
    });
    if (structuredData) recordStructuredData(structuredData);
  }

  useEffect(() => {

    // Update page title
    document.title = title;
    
    // Update meta description
    updateMetaTag('description', description);
    
    // Update keywords if provided
    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    updateMetaTag('robots', noindex ? 'noindex, follow' : 'index, follow');
    
    // Update canonical URL
    const canonicalUrl = canonical || `https://wtrrestoration.com${location.pathname}`;
    updateCanonical(canonicalUrl);
    
    const socialImage = openGraph?.image || DEFAULT_SOCIAL_IMAGE;

    // Every route must replace the homepage defaults. Lovable's crawler
    // prerenderer can only capture route-specific values that exist in the DOM.
    updateMetaProperty('og:title', openGraph?.title || title);
    updateMetaProperty('og:description', openGraph?.description || description);
    updateMetaProperty('og:url', canonicalUrl);
    updateMetaProperty('og:image', socialImage);
    updateMetaProperty('og:image:alt', openGraph?.imageAlt || title);
    updateMetaProperty('og:type', openGraph?.type || 'website');
    updateMetaProperty('og:site_name', 'WTR Restoration');
    updateMetaProperty('og:locale', 'en_US');
    
    // Update Twitter tags
    updateMetaName('twitter:card', 'summary_large_image');
    updateMetaName('twitter:title', openGraph?.title || title);
    updateMetaName('twitter:description', openGraph?.description || description);
    updateMetaName('twitter:image', socialImage);
    updateMetaName('twitter:image:alt', openGraph?.imageAlt || title);
    
    // Add structured data
    if (structuredData) {
      addStructuredData(structuredData);
    }
    
    // Cleanup function to remove structured data when component unmounts
    return () => {
      const existingScript = document.querySelector('script[data-seo-structured-data]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [title, description, keywords, canonical, noindex, openGraph, structuredData, location.pathname]);
};

const updateMetaTag = (name: string, content: string) => {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const updateMetaProperty = (property: string, content: string) => {
  let meta = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const updateMetaName = (name: string, content: string) => {
  let meta = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
};

const updateCanonical = (url: string) => {
  let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = url;
};

const addStructuredData = (data: object) => {
  // Remove existing structured data
  const existing = document.querySelector('script[data-seo-structured-data]');
  if (existing) {
    existing.remove();
  }
  
  // Add new structured data
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.setAttribute('data-seo-structured-data', 'true');
  script.textContent = JSON.stringify(data);
  document.head.appendChild(script);
};
